import { observable }     from 'mobx';
import { makeObservable } from 'mobx';
import { action }         from 'mobx';
import { computed }       from 'mobx';
import { jwtDecode }      from 'jwt-decode';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';

type RefreshTokenRequestReturn = Promise<{ token: string; refreshToken: string }>;

let refreshTokenPromise: undefined | RefreshTokenRequestReturn;

type Storage = {
	setItem: (key: string, value: string) => void;
	getItem: (key: string) => string | null;
	removeItem: (key: string) => void;
};

export class AuthStore {
	@observable private _token: string = '';
	@observable private _refreshToken: string = '';

	protected _tokenPrefix = '';
	protected _storage: Storage = localStorage;

	private _refreshTokenRequest: () => RefreshTokenRequestReturn = async () => ({ token: '', refreshToken: '' });

	constructor(storage: Storage = localStorage, tokenPrefix = '') {
		this._tokenPrefix = tokenPrefix;

		this.setStorage(storage);

		makeObservable(this);
	}

	setStorage(storage: Storage = localStorage) {
		this._storage = storage;
		this._token = storage.getItem(this.tokenKey) || '';
		this._refreshToken = storage.getItem(this.refreshTokenKey) || '';
	}

	setRefreshToken(newToken: string) {
		if (newToken) {
			this._storage.setItem(this.refreshTokenKey, newToken);
		} else {
			this._storage.removeItem(this.refreshTokenKey);
		}
		this._refreshToken = newToken;
	}

	@action setToken(newToken: string) {
		if (newToken) {
			this._storage.setItem(this.tokenKey, newToken);
		} else {
			this._storage.removeItem(this.tokenKey);
		}
		this._token = newToken;
	}

	@computed get tokenData(): any {
		return this._token ? jwtDecode(this._token) || {} : {};
	}

	@computed get exp() {
		return this.tokenData.exp as number;
	}

	@computed get token() {
		return this._token;
	}

	public isExpired() {
		return isExpired(this.exp);
	}

	public get refreshToken() {
		return this._refreshToken;
	}

	public clear() {
		this.setToken('');
		this.setRefreshToken('');
	}

	public setRefreshTokenRequest(request: () => RefreshTokenRequestReturn) {
		this._refreshTokenRequest = request;
	}

	public async refreshTokenAsync() {
		if (!refreshTokenPromise) {
			refreshTokenPromise = new Promise(resolve => {
				this._refreshTokenRequest().then(result => resolve(result));
			});
		}

		// Les requêtes suivantes vont attendre que le refreshToken en cours se termine
		const result = await refreshTokenPromise;

		if (this.isExpired()) {
			refreshTokenPromise = undefined;

			this.setToken(result.token);
			this.setRefreshToken(result.refreshToken);
		}
	}

	get tokenKey() {
		return [this._tokenPrefix, TOKEN_KEY].filter(Boolean).join('_');
	}

	get refreshTokenKey() {
		return [this._tokenPrefix, REFRESH_TOKEN_KEY].filter(Boolean).join('_');
	}
}

const isExpired = (exp: number) => (exp || 0) < Math.ceil(new Date().valueOf() / 1000);

export default new AuthStore();
